// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import type {
  BulkProspectCandidate,
  BulkProspectResolvePayload,
  BulkProspectCreatePayload,
  BulkProspectCreateResponse
} from '../../../state_manager/data_models/bulk_prospect_candidates_model.ts'
import { fetchBulkProspectCandidates, bulkCreateProspects, fetchFundraisingFiltered } from '../services/fundraisingService.ts'
import type { ProspectRecord } from '../../../state_manager/interfaces/fundraising.ts'

/**
 * useBulkAddProspects — the Bulk Add Prospects screen's own data layer.
 * Screen-local, NO Redux (the Send Communication precedent): the resolved
 * candidate list and its per-row edits are session state that dies with the
 * tab. Option universes (target fund, filter funds, LP statuses, stages) come
 * straight from the globalConfigs slice in the component.
 */
export const useBulkAddProspects = () => {
  const [candidates, setCandidates] = useState<BulkProspectCandidate[] | null>(null)
  const [resolving, setResolving] = useState(false)
  const [creating, setCreating] = useState(false)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  /** Generate: resolve the filters to the candidate LP set. Returns an error
   * message, or null on success. */
  const resolve = useCallback(async (payload: BulkProspectResolvePayload): Promise<string | null> => {
    setResolving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchBulkProspectCandidates(payload, token)
      if (cancelledRef.current) return null
      if (res.error) {
        setCandidates([])
        return res.error
      }
      setCandidates(res.data)
      return null
    } finally {
      if (!cancelledRef.current) setResolving(false)
    }
  }, [])

  /** Create the included rows atomically. Returns the service response so the
   * screen can distinguish 409 duplicate conflicts from other failures. */
  const create = useCallback(
    async (payload: BulkProspectCreatePayload): Promise<BulkProspectCreateResponse> => {
      setCreating(true)
      try {
        const token = getAccessToken(store.getState().auth)
        return await bulkCreateProspects(payload, token)
      } finally {
        if (!cancelledRef.current) setCreating(false)
      }
    },
    []
  )

  const clear = useCallback(() => setCandidates(null), [])

  return { candidates, resolving, creating, resolve, create, clear }
}

/**
 * useFundProspects — the target fund's EXISTING live prospects, shown as
 * context under the bulk-add grid. Screen-local (no Redux — the kanban owns
 * the fundraising pipeline slice). Reuses the lean POST /prospects/filter
 * call: a single prospects__fund filter, no report_id (skips the
 * reporting-grid enrichment fan-out) and no prospect_query_context (the
 * default table branch spans ALL fundraising pipelines, so SPV-pipeline
 * prospects are included; the pipeline_board context would drop stages not
 * visible on the kanban).
 */
const FUND_PROSPECTS_PAGE_SIZE = 50

export const useFundProspects = (fundId: string | undefined) => {
  const [prospects, setProspects] = useState<ProspectRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const pageRef = useRef(1)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const fetchPage = useCallback(
    async (page: number) => {
      const token = getAccessToken(store.getState().auth)
      return fetchFundraisingFiltered(
        [],
        [{ key: 'prospects__fund', value: [fundId as string] }],
        page,
        FUND_PROSPECTS_PAGE_SIZE,
        token
      )
    },
    [fundId]
  )

  const refetch = useCallback(async () => {
    if (!fundId) {
      setProspects([])
      setHasMore(false)
      return
    }
    setLoading(true)
    try {
      const res = await fetchPage(1)
      if (cancelledRef.current) return
      pageRef.current = 1
      setProspects(res.error ? [] : res.data)
      setHasMore(!res.error && res.hasMore)
    } finally {
      if (!cancelledRef.current) setLoading(false)
    }
  }, [fundId, fetchPage])

  /** Continuous scroll: append the next page when the list nears its end.
   * In-flight guard is a ref, not state — rapid scroll events would race a
   * state flag before it commits and double-fetch a page. Appends dedupe on
   * prospects__id (offset pagination over non-unique sort keys can overlap). */
  const loadingMoreRef = useRef(false)
  const loadMore = useCallback(async () => {
    if (!fundId || !hasMore || loading || loadingMoreRef.current) return
    loadingMoreRef.current = true
    setLoadingMore(true)
    try {
      const res = await fetchPage(pageRef.current + 1)
      if (cancelledRef.current || res.error) return
      pageRef.current += 1
      setProspects((prev) => {
        const seen = new Set(prev.map((p: any) => String(p.prospects__id)))
        return [...prev, ...res.data.filter((p: any) => !seen.has(String(p.prospects__id)))]
      })
      setHasMore(res.hasMore)
    } finally {
      loadingMoreRef.current = false
      if (!cancelledRef.current) setLoadingMore(false)
    }
  }, [fundId, hasMore, loading, fetchPage])

  /** Optimistic local merge after an inline edit (stage/commitment). */
  const updateLocal = useCallback((prospectId: string, patch: Record<string, unknown>) => {
    setProspects((prev) =>
      prev.map((p: any) => (String(p.prospects__id) === prospectId ? { ...p, ...patch } : p))
    )
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { prospects, loading, loadingMore, hasMore, refetch, loadMore, updateLocal }
}
